import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { PageHeader } from "@/pages/layoutChrome";
import { DESPACHO_CONTEXT_LABEL } from "@/modules/despacho/manifest";
import { DESPACHO_MOCK_NOTICE } from "@/modules/despacho/despachoMockData";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const COUNT_BADGE_TONES = {
  critical: "bg-rose-100 text-rose-900 border-rose-200",
  warn: "bg-amber-100 text-amber-900 border-amber-200",
  info: "bg-sky-100 text-sky-900 border-sky-200",
  neutral: "bg-slate-100 text-slate-800 border-slate-200",
};

const SECTION_TONES = {
  critical: "border-rose-200 bg-rose-50/40",
  warn: "border-amber-200 bg-amber-50/40",
  info: "border-sky-200 bg-sky-50/40",
};

export function DespachoCountBadge({ value, tone = "neutral" }) {
  return (
    <span
      className={`inline-flex min-w-[2rem] justify-center rounded-sm border px-2 py-0.5 text-sm font-bold tabular-nums ${COUNT_BADGE_TONES[tone] || COUNT_BADGE_TONES.neutral}`}
    >
      {value ?? 0}
    </span>
  );
}

export function DespachoMockBanner({ compact = false }) {
  if (compact) {
    return (
      <p
        className="text-[10px] text-slate-500 m-0 leading-relaxed"
        data-testid="despacho-mock-banner"
        role="status"
      >
        {DESPACHO_MOCK_NOTICE}
      </p>
    );
  }
  return (
    <div
      className="rounded-sm border border-slate-200 bg-slate-50/80 px-3 py-2 text-sm text-slate-700 flex items-start gap-2"
      data-testid="despacho-mock-banner"
      role="status"
    >
      <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-slate-500" aria-hidden />
      <p className="m-0 text-[10px] leading-relaxed">{DESPACHO_MOCK_NOTICE}</p>
    </div>
  );
}

export function DespachoInfoCallout({ children, testId }) {
  return (
    <p
      className="text-xs text-slate-600 m-0 rounded-sm border border-slate-200 bg-slate-50 px-4 py-3 leading-relaxed"
      data-testid={testId}
    >
      {children}
    </p>
  );
}

export function DespachoPageShell({ title, subtitle, children, testId }) {
  return (
    <div className="min-h-screen" data-testid={testId}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        breadcrumb={[DESPACHO_CONTEXT_LABEL, title]}
      />
      <div className="p-8 max-w-6xl space-y-6">{children}</div>
    </div>
  );
}

export function DespachoSectionCard({
  title,
  description,
  icon: Icon,
  tone,
  children,
  testId,
  headerActions,
  className = "",
}) {
  return (
    <section
      className={`rounded-sm border p-4 space-y-3 ${SECTION_TONES[tone] || "border-slate-200 bg-white"}${className ? ` ${className}` : ""}`}
      data-testid={testId}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {Icon ? <Icon className="w-4 h-4 text-[#0F2D5C] shrink-0" aria-hidden /> : null}
            <h2 className="text-sm font-bold text-[#0F2D5C] m-0">{title}</h2>
          </div>
          {description ? <p className="text-xs text-slate-500 mt-1 m-0">{description}</p> : null}
        </div>
        {headerActions ? (
          <div className="shrink-0 flex flex-wrap items-center justify-end gap-2 ml-auto">
            {headerActions}
          </div>
        ) : null}
      </div>
      {children}
    </section>
  );
}

/** @deprecated Usar DespachoSectionCard — alias de compatibilidad. */
export function DespachoPlaceholderPanel(props) {
  return <DespachoSectionCard {...props} />;
}

export function DespachoKpiCard({ label, value, tone = "neutral", href }) {
  const body = (
    <article className="relative flex flex-col rounded-sm border border-slate-200 bg-white p-4 min-h-[100px] transition-shadow hover:shadow-md hover:border-[#0F2D5C]/20">
      <p className="text-[11px] text-slate-600 m-0 leading-snug">{label}</p>
      <div className="mt-2">
        <DespachoCountBadge value={value} tone={tone} />
      </div>
    </article>
  );
  if (href) {
    return (
      <Link
        to={href}
        className="block no-underline text-inherit focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F2D5C]/30 rounded-sm"
      >
        {body}
      </Link>
    );
  }
  return body;
}

export function DespachoSimpleTable({ columns, rows, emptyLabel = "Sin registros de demostración." }) {
  if (!rows?.length) {
    return <p className="text-sm text-slate-600 m-0">{emptyLabel}</p>;
  }
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-sm bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead key={col.key} className={col.right ? "text-right" : ""}>
                {col.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={row.id || idx}>
              {columns.map((col) => (
                <TableCell key={col.key} className={col.right ? "text-right" : ""}>
                  {col.render ? col.render(row) : row[col.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function DespachoStatusBadge({ children, variant = "secondary" }) {
  return (
    <Badge variant={variant} className="font-normal text-[11px]">
      {children}
    </Badge>
  );
}
